import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Icon, type Column } from "@legal-500/legal500-design-system";
import { powerlistEditions, powerlistSeries, type Edition } from "../../api/powerlist.service";
import { DataTable } from "../../components/DataTable/DataTable";
import EmptyState from "../../components/EmptyState";
import { ErrorBanner } from "../../components/ErrorBanner/ErrorBanner";
import LoadingState from "../../components/LoadingState";
import { PageHeader } from "../../components/PageHeader";
import { StatusBadge } from "../../components/StatusBadge";
import { useQuery } from "../../hooks/useApi";
import { ROUTES } from "../../routes";
import { EditionFormModal } from "./EditionFormModal";
import { usePowerlistAccess, useToast } from "./shared";
import styles from "./Powerlist.module.css";

export default function SeriesEditionsPage() {
  const { seriesId = "" } = useParams();
  const navigate = useNavigate();
  const { canWrite } = usePowerlistAccess();
  const { data, loading, error, refetch, isRefetching } = useQuery(async () => {
    const [series, editions] = await Promise.all([
      powerlistSeries.get(seriesId),
      powerlistEditions.listForSeries(seriesId),
    ]);
    return { series, editions };
  }, [seriesId]);
  const [creating, setCreating] = useState(false);
  const { toast, success } = useToast();

  const columns: Column<Edition>[] = [
    { header: "Year", accessor: "year" },
    {
      header: "Title",
      accessor: "title",
      render: (_value: unknown, row: Edition) => (
        <Link to={ROUTES.powerlist.edition(row.id)}>{row.title}</Link>
      ),
    },
    { header: "Slug", accessor: "slug" },
    {
      header: "Status",
      accessor: "status",
      render: (_value: unknown, row: Edition) => <StatusBadge status={row.status} />,
    },
    {
      header: "Published",
      accessor: "publishedAt",
      render: (_value: unknown, row: Edition) =>
        row.publishedAt ? new Date(row.publishedAt).toLocaleString() : "—",
    },
  ];

  const editions = [...(data?.editions ?? [])].sort((a, b) => b.year - a.year);

  return (
    <div className={styles.wrapper}>
      <PageHeader title={data?.series.name ?? "Series"} subtitle="Editions in this series">
        <div className={styles.headerActions}>
          <Button
            variant="outlined"
            color="default"
            size="small"
            onClick={() => navigate(ROUTES.powerlist.series)}
          >
            <Icon icon="arrow-left" size="small" />
            All series
          </Button>
          {canWrite && data && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setCreating(true)}
            >
              New edition
            </Button>
          )}
        </div>
      </PageHeader>

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}

      {loading && data === null ? (
        <LoadingState message="Loading editions..." />
      ) : !error && editions.length === 0 ? (
        <EmptyState
          variant="empty"
          title="No editions"
          message="This series has no editions yet."
        />
      ) : (
        !error && (
          <div className={styles.tableContainer}>
            <DataTable data={editions} columns={columns} rowKey="id" emptyMessage="No editions." />
          </div>
        )
      )}

      {data && (
        <EditionFormModal
          isOpen={creating}
          series={data.series}
          edition={null}
          onClose={() => setCreating(false)}
          onSaved={edition => {
            setCreating(false);
            success(`${edition.title} created as a draft`);
            void refetch();
          }}
        />
      )}

      {toast}
    </div>
  );
}
